import { Box, Typography, useMediaQuery } from "@mui/material"
import { Download, Link2 } from "lucide-react"

// Coded mock UIs for the Pro section (spec §7). Static, non-interactive,
// each self-contained so it can later be swapped for an <img> in one line.

const card = {
    backgroundColor: '#fcfcfc',
    border: '1px solid #e8e8e8',
    borderRadius: '16px',
    p: '16px',
    fontFamily: 'Catamaran',
}

// ── Visual 1: Link Rankings leaderboard ─────────────────────────────────────

const LEADERBOARD_ROWS = [
    { rank: 1, name: 'Podcast', width: '92%', clicks: '2,118', trend: '↑ 41%', up: true },
    { rank: 2, name: 'Tip jar', width: '60%', clicks: '1,204', trend: '↑ 32%', up: true },
    { rank: 3, name: 'Merch', width: '34%', clicks: '733', trend: '↓ 8%', up: false },
    { rank: 4, name: 'Newsletter', width: '24%', clicks: '486', trend: '↑ 12%', up: true, desktopOnly: true },
]

export function LeaderboardVisual() {
    const isMobile = useMediaQuery('(max-width:950px)')
    const rows = isMobile ? LEADERBOARD_ROWS.filter(r => !r.desktopOnly) : LEADERBOARD_ROWS

    return (
        <Box
            role="img"
            aria-label="Vibio dashboard showing links ranked by clicks, with the creator's podcast unexpectedly in first place"
            sx={card}
        >
            <Box aria-hidden="true" sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {rows.map(row => (
                    <Box key={row.rank} sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography sx={{ fontFamily: 'inherit', color: '#6F6F76', fontSize: '0.8rem', fontWeight: 600, width: '0.9rem' }}>
                            {row.rank}
                        </Typography>
                        <Typography sx={{ fontFamily: 'inherit', color: '#222222', fontSize: '0.88rem', fontWeight: 700, width: isMobile ? '5rem' : '6.5rem', flexShrink: 0 }}>
                            {row.name}
                        </Typography>
                        <Box sx={{ flex: 1, height: '8px', borderRadius: '4px', backgroundColor: '#ECECEE', overflow: 'hidden' }}>
                            <Box sx={{ width: row.width, height: '100%', borderRadius: '4px', backgroundColor: 'primary.main' }} />
                        </Box>
                        <Typography sx={{ fontFamily: 'inherit', color: '#73737B', fontSize: '0.8rem', fontWeight: 600, width: '3rem', textAlign: 'right' }}>
                            {row.clicks}
                        </Typography>
                        <Typography sx={{ fontFamily: 'inherit', color: row.up ? '#38b000' : '#D64545', fontSize: '0.8rem', fontWeight: 700, width: '3.2rem', textAlign: 'right' }}>
                            {row.trend}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

// ── Visual 2: Revenue links list ────────────────────────────────────────────

const REVENUE_ROWS = [
    { name: 'Storefront', revenue: true, clicks: '1,982' },
    { name: 'Tip jar', revenue: true, clicks: '1,204' },
    { name: 'Instagram', revenue: false, clicks: '864' },
]

export function RevenueLinksVisual() {
    return (
        <Box
            role="img"
            aria-label="Vibio revenue links view: storefront and tip jar tagged as revenue links, with monthly revenue clicks up 27 percent"
            sx={card}
        >
            <Box aria-hidden="true">
                {REVENUE_ROWS.map((row, i) => (
                    <Box
                        key={row.name}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: '10px 4px',
                            borderBottom: i < REVENUE_ROWS.length - 1 ? '1px solid rgba(0,0,0,0.08)' : 'none',
                        }}
                    >
                        <Typography sx={{ fontFamily: 'inherit', color: '#222222', fontSize: '0.88rem', fontWeight: 700 }}>
                            {row.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {row.revenue && (
                                <Box sx={{
                                    backgroundColor: '#1e3d05',
                                    color: '#5aff6d',
                                    fontSize: '0.66rem',
                                    fontWeight: 700,
                                    borderRadius: '999px',
                                    p: '2px 8px',
                                }}>
                                    $
                                </Box>
                            )}
                            <Typography sx={{ fontFamily: 'inherit', color: '#73737B', fontSize: '0.8rem', fontWeight: 600 }}>
                                {row.clicks}
                            </Typography>
                        </Box>
                    </Box>
                ))}

                <Box sx={{ backgroundColor: '#F0F0F2', borderRadius: '8px', p: '10px 12px', mt: '12px' }}>
                    <Typography sx={{ fontFamily: 'inherit', color: '#6F6F76', fontSize: '0.68rem', fontWeight: 600, mb: '2px' }}>
                        <b>27%</b> of all clicks went to links that help you earn
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                        <Typography sx={{ fontFamily: 'inherit', color: '#222222', fontSize: '18px', fontWeight: 800 }}>
                            3,186 clicks
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

// ── Visual 3: Brand Report document (the finale) ────────────────────────────

const REPORT_STATS = [
    { label: 'Page views', value: '48.2k' },
    { label: 'Link clicks', value: '9.1k' },
    { label: 'CTR', value: '18.9%' },
    { label: 'Top link', value: 'Face Serum' },
]

const REPORT_LOCATIONS = [
    { flag: '🇺🇸', name: 'United States', share: '41%' },
    { flag: '🇬🇧', name: 'United Kingdom', share: '17%' },
    { flag: '🇩🇪', name: 'Germany', share: '9%' },
]

const REPORT_DEVICES = [
    { name: 'Mobile', share: '84%' },
    { name: 'Desktop', share: '16%' },
]

export function BrandReportVisual() {
    return (
        <Box
            role="img"
            aria-label="A sponsor-ready Vibio brand report for Modus Beauty: 48.2 thousand page views, 9.1 thousand link clicks, 18.9 percent click-through, top link face serum, audience mostly in the United States, 84 percent on mobile"
            sx={{
                ...card,
                border: '2px solid #FF1493',
            }}
        >
            <Box aria-hidden="true">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '14px' }}>
                    <Box sx={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#FFD0E9',
                        color: '#99005A',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 800,
                        fontSize: '1rem',
                        flexShrink: 0,
                    }}>
                        V
                    </Box>
                    <Box>
                        <Typography sx={{ fontFamily: 'inherit', color: '#222222', fontSize: '0.92rem', fontWeight: 500 }}>
                            May Report - Modus Beauty
                        </Typography>
                        <Typography sx={{ fontFamily: 'inherit', color: '#73737B', fontSize: '0.78rem', fontWeight: 500 }}>
                            vibio.co/charliex
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', mb: '10px' }}>
                    {REPORT_STATS.map(stat => (
                        <Box key={stat.label} sx={{ backgroundColor: '#F6F6F8', borderRadius: '8px', p: '10px 12px' }}>
                            <Typography sx={{ fontFamily: 'inherit', color: '#73737B', fontSize: '0.72rem', fontWeight: 600 }}>
                                {stat.label}
                            </Typography>
                            <Typography sx={{ fontFamily: 'inherit', color: '#222222', fontSize: '1.05rem', fontWeight: 800 }}>
                                {stat.value}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <Box sx={{ backgroundColor: '#F6F6F8', borderRadius: '8px', p: '10px 12px' }}>
                        <Typography sx={{ fontFamily: 'inherit', color: '#73737B', fontSize: '0.72rem', fontWeight: 600, mb: '4px' }}>
                            Top locations
                        </Typography>
                        {REPORT_LOCATIONS.map(loc => (
                            <Box key={loc.name} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                                <Typography sx={{ fontFamily: 'inherit', color: '#222222', fontSize: '0.78rem', fontWeight: 700 }}>
                                    <span style={{ marginRight: '4px' }}>{loc.flag}</span>
                                    {loc.name}
                                </Typography>
                                <Typography sx={{ fontFamily: 'inherit', color: '#73737B', fontSize: '0.78rem', fontWeight: 600 }}>
                                    {loc.share}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{ backgroundColor: '#F6F6F8', borderRadius: '8px', p: '10px 12px' }}>
                        <Typography sx={{ fontFamily: 'inherit', color: '#73737B', fontSize: '0.72rem', fontWeight: 600, mb: '4px' }}>
                            Devices
                        </Typography>
                        {REPORT_DEVICES.map(dev => (
                            <Box key={dev.name} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                                <Typography sx={{ fontFamily: 'inherit', color: '#222222', fontSize: '0.78rem', fontWeight: 700 }}>
                                    {dev.name}
                                </Typography>
                                <Typography sx={{ fontFamily: 'inherit', color: '#73737B', fontSize: '0.78rem', fontWeight: 600 }}>
                                    {dev.share}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}
